import { useCallback, useEffect, useMemo, useState } from 'react'
import { distanceToLineMeters } from '../lib/walkGeo'
import type { RouteState } from './routeUiState'
import type { GeoPosition } from './useGeolocation'

/** [A3 §6] Builder-tuned: how far off the planned line counts as "off route". */
export const OFF_ROUTE_M = { walking: 75, driving: 300 } as const
export const OFF_ROUTE_SUSTAIN_MS = 30_000
export type ReplanNudge = 'routingBack' | 'offRoute' | null

/**
 * A gentle, dismissible hint that pressing Re-plan is worth it. It never changes an order. Nothing for
 * a locked (hand-made) order. Dismissal holds until the condition clears and re-arises. All state is
 * tagged with the scope (turf + mode), so a switch starts clean.
 */
export function useReplanNudge(p: {
  scope: string; routeState: RouteState; routingBack: boolean
  routeLine: [number, number][] | null; position: GeoPosition | null
  mode: 'walking' | 'driving'; locked: boolean
}): { nudge: ReplanNudge; dismiss(): void; reset(): void } {
  const { scope, routeState, routingBack, routeLine, position, mode, locked } = p

  const offNow = useMemo(() => {
    if (locked || !position || !routeLine || routeLine.length < 2) return false
    return distanceToLineMeters([position.lng, position.lat], routeLine) > OFF_ROUTE_M[mode]
  }, [locked, position, routeLine, mode])

  // `sustainedFor` names the scope + routeState whose 30 s clock completed (stale tags read as false).
  // `resetCount` is the explicit Re-plan signal: an offline re-freeze leaves routeState unchanged.
  const [resetCount, setResetCount] = useState(0)
  const tag = `${scope}|${routeState}|${resetCount}`
  const [sustainedFor, setSustainedFor] = useState<string | null>(null)
  useEffect(() => {
    setSustainedFor(null) // every tag change restarts the 30 s clock
    if (!offNow) return
    const t = setTimeout(() => setSustainedFor(tag), OFF_ROUTE_SUSTAIN_MS)
    return () => clearTimeout(t)
  }, [offNow, tag])

  const raw: ReplanNudge = locked ? null
    : routingBack ? 'routingBack'
    : offNow && sustainedFor === tag ? 'offRoute'
    : null

  const [dismissed, setDismissed] = useState<{ scope: string; kind: Exclude<ReplanNudge, null> } | null>(null)
  // Condition cleared (or scope changed) -> the dismissal is spent.
  useEffect(() => {
    if (dismissed && (dismissed.scope !== scope || dismissed.kind !== raw)) setDismissed(null)
  }, [dismissed, scope, raw])
  // A Re-plan (the order/state changed) also re-arms.
  useEffect(() => { setDismissed(null) }, [routeState, resetCount])

  const nudge = raw && dismissed && dismissed.scope === scope && dismissed.kind === raw ? null : raw
  const dismiss = useCallback(() => { if (raw) setDismissed({ scope, kind: raw }) }, [raw, scope])
  const reset = useCallback(() => setResetCount(c => c + 1), [])
  return { nudge, dismiss, reset }
}
